import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Trash2, ShoppingCart, User, LogOut, ShoppingBag, History, Clock, X } from 'lucide-react';
import { CartProvider, useCart } from '../features/customer/context/CartContext';
import CustomerHeader from '../features/customer/components/CustomerHeader';
import ProductList from '../features/customer/components/ProductList';
import RecentOrdersTab from '../features/customer/components/RecentOrdersTab';
import CartDrawer from '../features/customer/components/CartDrawer';
import LoadingSpinner from '../components/LoadingSpinner';
import TokenExpiredError from '../components/TokenExpiredError';
import CustomerSidebar from '../features/customer/components/CustomerSidebar';
import CustomerBottomNavbar from '../features/customer/components/CustomerBottomNavbar';
import ConfirmationModal from '../components/common/Modal/ConfirmationModal';
import { orderSessionService } from '../services/api/orderSessionService';
import { publicProductService } from '../services/api/publicProductService';
import { publicOrderService } from '../services/api/publicOrderService';
import { publicCustomerService } from '../services/api/publicCustomerService';
import { publicTenantService } from '../services/api/publicTenantService';

const OrderContent = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const rawToken = searchParams.get('token');
    const token = (rawToken && rawToken !== 'null' && rawToken !== 'undefined') ? rawToken : null;
    const activeToken = token || localStorage.getItem('customer_token') || sessionStorage.getItem('customer_token');

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isClearCartModalOpen, setIsClearCartModalOpen] = useState(false);
    const [sessionData, setSessionData] = useState(null);
    const [customerData, setCustomerData] = useState(null);
    const [supplierData, setSupplierData] = useState(null);
    const [products, setProducts] = useState([]);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [activeTab, setActiveTab] = useState('All');
    const [selectedCategory, setSelectedCategory] = useState('All');
    const [productCategories, setProductCategories] = useState([]);
    const [isCartOpen, setIsCartOpen] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [deliverySchedules, setDeliverySchedules] = useState([]);
    const [frequentProductIds, setFrequentProductIds] = useState([]);
    const [visibleCount, setVisibleCount] = useState(15);

    const recentCategories = React.useMemo(() => {
        const categories = [];
        frequentProductIds.forEach(id => {
            const prod = products.find(p => p.id === id);
            if (prod && prod.category && !categories.includes(prod.category)) {
                categories.push(prod.category);
            }
        });
        const topRecent = categories.slice(0, 5);
        if (topRecent.length < 5) {
            productCategories.forEach(cat => {
                if (!topRecent.includes(cat) && topRecent.length < 5) {
                    topRecent.push(cat);
                }
            });
        }
        return topRecent;
    }, [frequentProductIds, products, productCategories]);

    const { totalItems, totalAmount, clearCart, replaceCart, totalSavings, cart = [] } = useCart();

    // Handle reordered items from localStorage
    useEffect(() => {
        const reorderStr = localStorage.getItem('yaseero_reorder_items');
        const openCartParam = searchParams.get('openCart');
        let shouldCleanUrl = false;

        if (reorderStr) {
            try {
                const items = JSON.parse(reorderStr);
                if (Array.isArray(items) && items.length > 0) {
                    replaceCart(items);
                    setIsCartOpen(true);
                    shouldCleanUrl = true;
                }
            } catch (err) {
                console.error('[OrderPage] Failed to load reordered items:', err);
            } finally {
                localStorage.removeItem('yaseero_reorder_items');
            }
        } else if (openCartParam === 'true') {
            setIsCartOpen(true);
            shouldCleanUrl = true;
        }

        if (shouldCleanUrl && openCartParam === 'true') {
            const newParams = new URLSearchParams(searchParams);
            newParams.delete('openCart');
            setSearchParams(newParams, { replace: true });
        }
    }, [searchParams, setSearchParams, replaceCart]);

    // Validate token and load session context
    useEffect(() => {
        const loadSessionContext = async () => {
            let activeToken = token;
            
            // If no token in URL or if it is "null"/"undefined", try to get from storage
            if (!activeToken || activeToken === 'null' || activeToken === 'undefined') {
                activeToken = localStorage.getItem('customer_token') || sessionStorage.getItem('customer_token');
            }

            if (!activeToken || activeToken === 'null' || activeToken === 'undefined') {
                console.log('[OrderPage] No token found, redirecting to login...');
                navigate('/order/login');
                return;
            }

            try {
                console.log('[OrderPage] Loading session context with token:', activeToken);
                const response = await orderSessionService.getSessionContext(activeToken);

                if (!response.valid) {
                    // If token is invalid, clear storage and redirect
                    localStorage.removeItem('customer_token');
                    sessionStorage.removeItem('customer_token');
                    setError(response.errorMessage || 'Invalid or expired token');
                    setLoading(false);
                    return;
                }

                console.log('[OrderPage] Session context loaded:', response);

                // 1. Set Session Info
                const sessionInfo = {
                    customerId: response.customer?.publicId,
                    supplierPublicId: response.tenant?.supplierId, // TenantDTO has supplierId
                    expiresAt: response.expiresAt,
                    token: activeToken
                };
                setSessionData(sessionInfo);

                // 2. Set Customer & Supplier Data
                // Map backend response to frontend expectations if needed
                // CustomerDTO matches what we need mostly
                setCustomerData(response.customer);

                // TenantResponseDTO has businessName, etc.
                setSupplierData(response.tenant);

                // 3. Set Delivery Schedules from session context
                setDeliverySchedules(response.deliverySchedules || []);

                // Clean token from URL query parameters so back-button doesn't auto-relogin
                if (token) {
                    if (token !== 'null' && token !== 'undefined') {
                        localStorage.setItem('customer_token', token);
                    }
                    const newParams = new URLSearchParams(searchParams);
                    newParams.delete('token');
                    setSearchParams(newParams, { replace: true });
                }

            } catch (err) {
                console.error('[OrderPage] Session context load failed:', err);
                setError('Failed to load order session. Please try again or contact your supplier.');
                setLoading(false);
            }
        };

        loadSessionContext();
    }, [token, searchParams, setSearchParams, navigate]);

    // Load products when session is available
    useEffect(() => {
        const loadProducts = async () => {
            if (!sessionData) return;

            try {
                console.log('[OrderPage] Loading products...');
                const response = await publicProductService.searchProducts(
                    sessionData.supplierPublicId,
                    sessionData.customerId,
                    '',
                    0,
                    1000
                );
                // ... rest of product loading logic remains same

                console.log('[OrderPage] Products loaded:', response);

                // Transform ProductDTO to match existing UI expectations
                const transformedProducts = response.content
                    .filter(p => p.active) // Only show active products
                    .map(product => {
                        const originalPrice = product.effectivePrice || product.salesPriceRetail || 0;
                        const price = product.effectiveDiscountedPrice || product.effectivePrice || product.salesPriceRetail || 0;
                        const hasDiscount = !!product.effectiveDiscountedPrice && product.effectiveDiscountedPrice < originalPrice;
                        return {
                            id: product.publicId,
                            name: product.name,
                            price: price,
                            originalPrice: originalPrice,
                            hasDiscount: hasDiscount,
                            unit: product.unit || 'Unit',
                            defaultUnit: product.unit || 'Unit',
                            image: product.imageUrl || null, // Map S3 image URL if available
                            category: product.category || product.categoryName || 'Uncategorized',
                            availableQuantity: product.availableQuantity,
                            vatRate: product.vatRate !== undefined ? product.vatRate : 5,
                            priceIncludesVat: product.priceIncludesVat !== undefined ? product.priceIncludesVat : false
                        };
                    });

                const uniqueCategories = [...new Set(transformedProducts.map(p => p.category))].sort();
                setProductCategories(uniqueCategories);

                setProducts(transformedProducts);
                setLoading(false);
            } catch (err) {
                console.error('[OrderPage] Failed to load products:', err);
                setError('Failed to load products. Please try again.');
                setLoading(false);
            }
        };

        loadProducts();
    }, [sessionData]);

    // Load customer order history to extract frequent products
    useEffect(() => {
        const loadFrequentProducts = async () => {
            if (!sessionData) return;
            try {
                // Fetch the customer's last 50 orders
                const response = await publicOrderService.getCustomerOrders(
                    sessionData.customerId,
                    sessionData.supplierPublicId,
                    0,
                    50
                );
                const rawOrders = Array.isArray(response) ? response : (response.content || []);
                
                // Count frequencies
                const counts = {};
                rawOrders.forEach(order => {
                    if (order.items && Array.isArray(order.items)) {
                        order.items.forEach(item => {
                            const pid = item.productPublicId || item.productId;
                            if (pid) {
                                counts[pid] = (counts[pid] || 0) + 1;
                            }
                        });
                    }
                });
                
                // Sort by frequency descending
                const sortedIds = Object.keys(counts).sort((a, b) => counts[b] - counts[a]);
                setFrequentProductIds(sortedIds);
            } catch (err) {
                console.error('[OrderPage] Failed to load frequent products:', err);
            }
        };

        loadFrequentProducts();
    }, [sessionData]);

    // Filter products based on search, tab, and category
    useEffect(() => {
        const filtered = products.filter(product => {
            const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase());
            
            // Tab logic
            let matchesTab = false;
            if (activeTab === 'All') matchesTab = true;
            else if (activeTab === 'Offers') matchesTab = product.hasDiscount;
            else if (activeTab === 'Frequent') {
                matchesTab = frequentProductIds.includes(product.id);
            }
            
            let matchesCat = false;
            if (selectedCategory === 'All') {
                matchesCat = true;
            } else if (selectedCategory === 'Selected') {
                matchesCat = cart.some(item => item.id === product.id);
            } else {
                matchesCat = product.category === selectedCategory;
            }
            
            return matchesSearch && matchesTab && matchesCat;
        });

        if (activeTab === 'Frequent') {
            filtered.sort((a, b) => {
                return frequentProductIds.indexOf(a.id) - frequentProductIds.indexOf(b.id);
            });
        }

        setFilteredProducts(filtered);
        setVisibleCount(15);
    }, [products, searchQuery, activeTab, selectedCategory, frequentProductIds, cart]);

    const handleScroll = (e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        if (scrollHeight - scrollTop - clientHeight < 100) {
            if (visibleCount < filteredProducts.length) {
                setVisibleCount(prev => prev + 15);
            }
        }
    };

    // Sync activeTab with search parameter query 'tab'
    useEffect(() => {
        const tabParam = searchParams.get('tab');
        if (tabParam) {
            setActiveTab(tabParam === 'Pending' ? 'Pending Orders' : 'All');
        }
    }, [searchParams]);

    // Handle order submission
    const handleSubmitOrder = async (orderData) => {
        if (!sessionData) {
            throw new Error('Session data not available');
        }

        // Transform cart items to match PlaceOrderRequest format
        const orderRequest = {
            supplierPublicId: sessionData.supplierPublicId,
            customerPublicId: sessionData.customerId,
            deliveryDate: orderData.deliveryDate || null,
            timeSlot: orderData.timeSlot || null,
            items: orderData.items.map(item => ({
                productPublicId: item.id,
                quantity: item.quantity
            }))
        };

        console.log('[OrderPage] Placing order:', orderRequest);
        const response = await publicOrderService.placeOrder(orderRequest);
        console.log('[OrderPage] Order placed successfully:', response);

        // Clear the search criteria on successful order placement
        setSearchQuery('');

        return response;
    };

    if (loading) {
        return <LoadingSpinner message="Validating your session..." />;
    }

    if (error) {
        return <TokenExpiredError message={error} />;
    }

    return (
        <div className="flex h-[100dvh] w-screen bg-gray-50 overflow-hidden font-sans">
            {/* Desktop Left Sidebar */}
            <CustomerSidebar 
                activeView={activeTab === 'Pending Orders' ? 'pending' : 'shop'}
                token={activeToken}
                customerData={customerData}
                supplierData={supplierData}
                onTabChange={setActiveTab}
            />

            {/* Main Content Pane */}
            <div className="flex-1 flex flex-col min-w-0 h-[100dvh] relative pb-customer-nav lg:pb-0">
                {/* Header - Optimized for fitted look */}
                <div className="bg-white border-b px-4 py-1.5 md:px-6 md:py-2 flex-none z-10">
                    <CustomerHeader
                        supplierData={supplierData}
                        searchQuery={searchQuery}
                        setSearchQuery={setSearchQuery}
                        activeTab={activeTab}
                        setActiveTab={setActiveTab}
                        onOpenCart={() => setIsCartOpen(true)}
                        productCategories={productCategories}
                        recentCategories={recentCategories}
                        selectedCategory={selectedCategory}
                        setSelectedCategory={setSelectedCategory}
                        // Omitted onOpenMenu to hide hamburger menu, handled by sidebar/bottom navbar
                        containerClassName="w-full"
                    />
                </div>

                {/* Products Grid / Orders Grid - Optimized for fitted look */}
                <div className={`flex-1 overflow-hidden flex flex-col transition-all duration-200 ${
                    activeTab === 'Pending Orders' ? 'p-4 md:p-6 bg-slate-50/50' : 'p-1.5 md:p-2 bg-gray-50'
                }`}>
                    <div 
                        onScroll={handleScroll}
                        className="flex-1 min-h-0 overflow-y-auto custom-scrollbar"
                    >
                        {activeTab === 'Pending Orders' ? (
                            <RecentOrdersTab 
                                customerId={sessionData?.customerId} 
                                supplierId={sessionData?.supplierPublicId} 
                                token={activeToken} 
                                searchQuery={searchQuery}
                            />
                        ) : activeTab === 'Frequent' && frequentProductIds.length === 0 ? (
                            <div className="text-center py-16 bg-white border border-slate-100 rounded-3xl p-8 shadow-sm max-w-md mx-auto mt-8">
                                <div className="bg-slate-50 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-slate-100">
                                    <Clock className="w-8 h-8 text-slate-400" />
                                </div>
                                <h3 className="text-lg font-bold text-slate-900 mb-1">No Frequent Items Yet</h3>
                                <p className="text-slate-400 text-sm">Once you start ordering products, your most frequently purchased items will show up here.</p>
                            </div>
                        ) : (
                            <>
                                <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 px-1.5 mt-1">
                                    Showing {Math.min(visibleCount, filteredProducts.length)} of {filteredProducts.length} products
                                </div>
                                <ProductList
                                    products={filteredProducts.slice(0, visibleCount)}
                                    className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4 pb-24"
                                    isCustomer={true}
                                />
                                {visibleCount < filteredProducts.length && (
                                    <div className="py-4 flex justify-center items-center text-slate-400 text-xs gap-2">
                                        <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-cyan-500"></span>
                                        <span>Loading more products...</span>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>

                {/* Bottom Cart Bar (Positioned above bottom nav on mobile/tablet) */}
                {totalItems > 0 && (
                    <div className="absolute bottom-16 lg:bottom-0 left-0 right-0 bg-white border-t items-end shadow-lg z-20 pb-safe pb-4 sm:pb-3">
                        <div className="w-full max-w-4xl mx-auto px-4 py-3">
                            <div className="flex items-center justify-between gap-3">
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-0.5">
                                        <span className="text-xs text-gray-500 font-medium">{totalItems} Items</span>
                                        {parseFloat(totalSavings) > 0 && (
                                            <span className="text-[10px] font-black text-emerald-600 bg-emerald-50 border border-emerald-100/50 px-2 py-0.5 rounded-full">
                                                You saved AED {totalSavings}
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-xl font-bold text-gray-800 truncate">AED {totalAmount}</p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => setIsClearCartModalOpen(true)}
                                        className="bg-red-50 text-red-600 p-2.5 rounded-xl font-bold hover:bg-red-100 transition-all shadow-sm hover:shadow-md flex items-center justify-center"
                                        title="Clear Cart"
                                    >
                                        <Trash2 size={20} />
                                    </button>
                                    <button
                                        onClick={() => setIsCartOpen(true)}
                                        className="bg-cyan-500 text-white px-6 py-3 rounded-lg font-medium hover:bg-cyan-600 transition-colors text-sm whitespace-nowrap shadow-lg shadow-cyan-500/30"
                                    >
                                        Review & Send
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Mobile / Tablet Bottom Navigation */}
                <CustomerBottomNavbar 
                    activeView={activeTab === 'Pending Orders' ? 'pending' : 'shop'}
                    token={activeToken}
                    supplierData={supplierData}
                    onTabChange={setActiveTab}
                />
            </div>

            <CartDrawer
                isOpen={isCartOpen}
                onClose={() => setIsCartOpen(false)}
                onSubmitOrder={handleSubmitOrder}
                sessionData={sessionData}
                customerData={customerData}
                supplierData={supplierData}
                token={activeToken}
                deliverySchedules={deliverySchedules}
            />

            <ConfirmationModal
                isOpen={isClearCartModalOpen}
                onClose={() => setIsClearCartModalOpen(false)}
                onConfirm={clearCart}
                title="Clear Cart?"
                message="Are you sure you want to remove all items from your shopping bag? This action cannot be undone."
                confirmText="Yes, Clear All"
                cancelText="No, Keep Items"
                type="info"
                icon={<ShoppingCart className="w-6 h-6 text-cyan-600" />}
            />
        </div>
    );
};

const OrderPage = () => {
    return (
        <CartProvider>
            <OrderContent />
        </CartProvider>
    );
};

export default OrderPage;
